import { useState, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { ChevronRight, Clock } from 'lucide-react'
import { EmptyState, StatusBadge, TableRowActions, SortableTh, TableFilterBar, TablePagination } from '@/shared/components'
import { useProgrammes, useDeleteProgramme } from '../hooks/use-programmes'
import { useTableSort } from '@/shared/hooks/use-table-sort'
import { usePagination } from '@/shared/hooks/use-pagination'
import { cn } from '@/lib/utils'
import { ConfirmDialog } from '@/shared/components/confirm-dialog'
import type { Programme, ProgrammeAction } from '@/shared/types/admin'

function formatJour(jour: number): string {
  if (jour === 0) return 'J0'
  if (jour > 0) return `J+${jour}`
  return `J${jour}`
}

function formatDuration(minutes: number): string {
  const h = Math.floor(minutes / 60)
  const m = minutes % 60
  if (h === 0) return `${m}min`
  if (m === 0) return `${h}h`
  return `${h}h${m}`
}

function getActionLabel(pa: ProgrammeAction): string {
  return pa.action?.libelle || `Action #${pa.actionId || '?'}`
}

function ProgrammeRow({ programme, onDelete }: { programme: Programme; onDelete: () => void }) {
  const { t } = useTranslation()
  const [expanded, setExpanded] = useState(false)
  const actionCount = programme.actions?.length || 0

  const sortedActions = [...(programme.actions || [])].sort(
    (a, b) => a.jourDeclenchement - b.jourDeclenchement,
  )

  return (
    <>
      <tr
        className={cn(
          'cursor-pointer transition-colors hover:bg-neutral-50',
          !programme.actif && 'opacity-50',
        )}
        onClick={() => actionCount > 0 && setExpanded(!expanded)}
      >
        <td className="w-8 px-2 py-3 text-center">
          {actionCount > 0 && (
            <ChevronRight
              className={cn(
                'h-4 w-4 text-neutral-400 transition-transform',
                expanded && 'rotate-90',
              )}
            />
          )}
        </td>
        <td className="px-4 py-3 text-sm font-medium text-neutral-900">
          {programme.libelle}
        </td>
        <td className="px-4 py-3">
          <StatusBadge
            label={programme.type === 'FORMATEUR' ? t('programmes.typeFormateur', 'Formateur') : t('programmes.typeEtudiant', 'Étudiant')}
            variant={programme.type === 'FORMATEUR' ? 'info' : 'warning'}
          />
        </td>
        <td className="px-4 py-3 text-sm text-neutral-500">
          {actionCount} {actionCount === 1 ? 'action' : 'actions'}
        </td>
        <td className="px-4 py-3">
          {programme.actif ? (
            <StatusBadge label="Actif" variant="active" />
          ) : (
            <StatusBadge label="Inactif" variant="inactive" />
          )}
        </td>
        <td className="px-4 py-3 text-right" onClick={(e) => e.stopPropagation()}>
          <TableRowActions
            editTo={`/programmes/${programme.numeroProgramme}`}
            onDelete={onDelete}
          />
        </td>
      </tr>
      {expanded && actionCount > 0 && (
        <tr>
          <td colSpan={6} className="bg-neutral-50/70 px-4 py-3">
            <div className="ml-6 space-y-1.5">
              {sortedActions.map((pa, i) => (
                <div
                  key={pa.id || i}
                  className="flex items-center gap-3 rounded-lg border border-neutral-100 bg-white px-3 py-2 text-xs"
                >
                  <span className="inline-flex w-8 shrink-0 items-center justify-center rounded bg-neutral-100 px-1 py-0.5 font-mono text-[10px] font-bold text-neutral-600">
                    {formatJour(pa.jourDeclenchement)}
                  </span>
                  <span className="flex-1 font-medium text-neutral-700">
                    {getActionLabel(pa)}
                  </span>
                  {pa.action?.dureeMinutes != null && pa.action.dureeMinutes > 0 && (
                    <span className="flex shrink-0 items-center gap-1 text-neutral-400">
                      <Clock className="h-3 w-3" />
                      {formatDuration(pa.action.dureeMinutes)}
                    </span>
                  )}
                </div>
              ))}
            </div>
          </td>
        </tr>
      )}
    </>
  )
}

export function ProgrammeList() {
  const { t } = useTranslation()
  const { data: programmes, isLoading } = useProgrammes()
  const deleteProgramme = useDeleteProgramme()
  const [statusFilter, setStatusFilter] = useState('all')
  const [typeFilter, setTypeFilter] = useState('all')

  const filtered = useMemo(() => {
    if (!programmes) return []
    let result = programmes
    if (statusFilter === 'actif') result = result.filter((p) => p.actif)
    if (statusFilter === 'inactif') result = result.filter((p) => !p.actif)
    if (typeFilter !== 'all') result = result.filter((p) => p.type === typeFilter)
    return result
  }, [programmes, statusFilter, typeFilter])

  const { sortedData, sortKey, sortDirection, handleSort } = useTableSort(filtered, 'libelle')
  const { paginatedData, currentPage, totalPages, totalItems, pageSize, setCurrentPage } = usePagination(sortedData)

  const [deleteDialog, setDeleteDialog] = useState<{ id: number; label: string } | null>(null)

  const handleDelete = (id: number, libelle: string) => {
    setDeleteDialog({ id, label: libelle })
  }

  const handleDeleteConfirm = () => {
    if (deleteDialog) {
      deleteProgramme.mutate(deleteDialog.id)
      setDeleteDialog(null)
    }
  }

  if (isLoading) {
    return (
      <div className="flex justify-center py-12">
        <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent" />
      </div>
    )
  }

  if (!programmes || programmes.length === 0) {
    return <EmptyState message={t('programmes.noProgrammes', 'Aucun programme disponible. Créez votre premier programme !')} />
  }

  const counts = {
    all: programmes.length,
    actif: programmes.filter((p) => p.actif).length,
    inactif: programmes.filter((p) => !p.actif).length,
  }

  const formateurCount = programmes.filter((p) => p.type === 'FORMATEUR').length
  const etudiantCount = programmes.filter((p) => p.type === 'ETUDIANT').length

  return (
    <>
      <TableFilterBar
        filters={[
          {
            key: 'status',
            label: 'Statut',
            value: statusFilter,
            options: [
              { value: 'all', label: 'Tous', count: counts.all },
              { value: 'actif', label: 'Actifs', count: counts.actif },
              { value: 'inactif', label: 'Inactifs', count: counts.inactif },
            ],
          },
          {
            key: 'type',
            label: 'Type',
            value: typeFilter,
            options: [
              { value: 'all', label: 'Tous', count: programmes.length },
              { value: 'FORMATEUR', label: 'Formateur', count: formateurCount },
              { value: 'ETUDIANT', label: 'Étudiant', count: etudiantCount },
            ],
          },
        ]}
        onFilterChange={(key, value) => {
          if (key === 'status') setStatusFilter(value)
          if (key === 'type') setTypeFilter(value)
        }}
      />
      <div className="overflow-hidden rounded-xl border border-neutral-200 bg-white">
        <table className="w-full">
          <thead>
            <tr className="border-b border-neutral-200 bg-gradient-to-r from-primary/5 to-transparent">
              <th className="w-8 px-2 py-3" />
              <SortableTh label="Programme" sortKey="libelle" currentSort={sortKey} currentDirection={sortDirection} onSort={handleSort} />
              <SortableTh label="Type" sortKey="type" currentSort={sortKey} currentDirection={sortDirection} onSort={handleSort} />
              <SortableTh label="Actions" sortKey="actions" currentSort={sortKey} currentDirection={sortDirection} onSort={handleSort} />
              <SortableTh label="Statut" sortKey="actif" currentSort={sortKey} currentDirection={sortDirection} onSort={handleSort} />
              <th className="px-4 py-3 text-right text-xs font-semibold uppercase tracking-wider text-neutral-400" />
            </tr>
          </thead>
          <tbody className="divide-y divide-neutral-100">
            {paginatedData.map((programme) => (
              <ProgrammeRow
                key={programme.numeroProgramme}
                programme={programme}
                onDelete={() => handleDelete(programme.numeroProgramme, programme.libelle)}
              />
            ))}
          </tbody>
        </table>
        <TablePagination currentPage={currentPage} totalPages={totalPages} totalItems={totalItems} pageSize={pageSize} onPageChange={setCurrentPage} />
      </div>

      <ConfirmDialog
        open={!!deleteDialog}
        variant="danger"
        title={`Supprimer le programme "${deleteDialog?.label}" ?`}
        description="Cette action est irréversible."
        confirmLabel="Supprimer"
        onConfirm={handleDeleteConfirm}
        onCancel={() => setDeleteDialog(null)}
      />
    </>
  )
}
